import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense, Component } from 'react';
import axios from 'axios';
import AppLayout from './layout/AppLayout';
import EmptyState from './layout/EmptyState';
import UpdatePopup from './components/UpdatePopup';
import { LanguageProvider } from './i18n';
import { AuthProvider, useAuth } from './AuthContext';
import { RealtimeProvider } from './RealtimeContext';
import { ServersProvider } from './servers/ServersContext';
import { rememberReturnTo } from './returnTo';
import TwoFactorSetup from './components/TwoFactorSetup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import './index.css';

const CHUNK_RELOAD_KEY = 'chunk_reload_at';

class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(error) {
    // After a deploy, old tabs may request chunks that no longer exist: reload once to get the new build
    const isChunkError = error?.name === 'ChunkLoadError' || /Loading (CSS )?chunk .* failed/.test(error?.message || '');
    if (!isChunkError) return;
    let last = 0;
    try { last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0; } catch {}
    if (Date.now() - last > 60000) {
      try { sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now())); } catch {}
      window.location.reload();
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', background: '#000', color: '#f43f5e', padding: '40px 24px', fontFamily: 'monospace', fontSize: '13px' }}>
          <div style={{ color: '#9b5de5', fontFamily: 'Nunito', fontSize: '24px', fontWeight: 900, marginBottom: '16px' }}>BLOBL</div>
          Something went wrong: {this.state.error.message}
        </div>
      );
    }
    return this.props.children;
  }
}

// A lazy page that can be loaded ahead of time. Once loaded it renders right away (React reads the
// already settled thenable), so a page sliding in never shows up empty while its code loads.
function preloadable(load, pick = (m) => m.default) {
  let loaded = null;
  let promise = null;
  const preload = () => promise || (promise = load().then(m => { loaded = { default: pick(m) }; return loaded; }));
  const Page = lazy(() => (loaded ? { status: 'fulfilled', value: loaded, then: (resolve) => resolve(loaded) } : preload()));
  Page.preload = preload;
  return Page;
}

const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const loadChatPages = () => import('./dm/ChatPages');
const Profile = preloadable(() => import('./pages/Profile'));
const Settings = preloadable(() => import('./pages/Settings'));
const EditProfile = preloadable(() => import('./pages/EditProfile'));
const ConversationPage = preloadable(loadChatPages, m => m.ConversationPage);
const UserChatPage = preloadable(loadChatPages, m => m.UserChatPage);
const loadServerPages = () => import('./servers/ServerPages');
const ServerHome = preloadable(loadServerPages, m => m.ServerHome);
const ChannelPage = preloadable(loadServerPages, m => m.ChannelPage);
const InvitePage = preloadable(() => import('./servers/InvitePage'));
// Pages of the signed-in app, loaded in the background after it started
const APP_PAGES = [ConversationPage, UserChatPage, Profile, Settings, EditProfile, ServerHome, ChannelPage, InvitePage];
const AdminPage = lazy(() => import('./pages/AdminPage'));
const InfoPage = lazy(() => import('./pages/InfoPage'));

function SplashScreen({ fading }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      animation: fading ? 'fadeOut 0.35s ease forwards' : 'fadeIn 0.3s ease',
    }}>
      <div style={{
        fontFamily: 'Nunito', fontSize: '52px', fontWeight: 900, letterSpacing: '6px',
        background: 'linear-gradient(135deg, var(--purple), var(--purple-light))',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        animation: 'splashPulse 1.2s ease-in-out infinite',
      }}>
        BLOBL
      </div>
    </div>
  );
}

function TimeoutScreen({ timeout }) {
  const { refreshUser } = useAuth();
  const [remaining, setRemaining] = useState('');
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, new Date(timeout.expires_at) - Date.now());
      if (diff === 0) { setExpired(true); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(
        h > 0
          ? `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`
          : `${m}m ${String(s).padStart(2, '0')}s`
      );
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [timeout.expires_at]);

  return (
    <div style={{ minHeight: '100vh', background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", textAlign: 'center' }}>
      <div style={{ fontSize: '56px', marginBottom: '20px' }}>🔒</div>
      <div style={{ fontFamily: 'Nunito', fontSize: '24px', fontWeight: 900, color: '#f0f0f0', marginBottom: '8px' }}>
        You are temporarily suspended
      </div>
      {timeout.reason && (
        <div style={{ color: '#888', fontSize: '14px', maxWidth: '320px', marginBottom: '24px', lineHeight: 1.6 }}>
          Reason: <span style={{ color: '#c084fc' }}>{timeout.reason}</span>
        </div>
      )}
      {!expired ? (
        <div style={{ background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: '16px', padding: '20px 36px' }}>
          <div style={{ fontSize: '11px', color: '#444', marginBottom: '6px', letterSpacing: '1px', textTransform: 'uppercase' }}>Time remaining</div>
          <div style={{ fontFamily: 'Nunito', fontSize: '32px', fontWeight: 800, color: '#f43f5e', letterSpacing: '2px' }}>{remaining}</div>
        </div>
      ) : (
        <button
          onClick={refreshUser}
          style={{ background: 'linear-gradient(135deg, #9b5de5, #7c3aed)', border: 'none', borderRadius: '25px', padding: '14px 32px', color: 'white', fontFamily: 'Nunito', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}
        >
          Continue
        </button>
      )}
    </div>
  );
}

// Moderators must enable 2FA before they can use the app
function TwoFactorRequiredScreen() {
  const { refreshUser, logout } = useAuth();
  return (
    <div style={{ minHeight: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div style={{ width: '100%', maxWidth: '380px' }}>
        <div style={{ fontFamily: 'Nunito', fontSize: '22px', fontWeight: 900, color: '#f0f0f0', marginBottom: '8px' }}>
          Two-factor authentication required
        </div>
        <div style={{ color: '#888', fontSize: '14px', lineHeight: 1.6, marginBottom: '20px' }}>
          Moderator accounts must be protected with an authenticator app. Set it up to continue.
        </div>
        <TwoFactorSetup onEnabled={refreshUser} />
        <button onClick={logout} style={{ marginTop: '16px', width: '100%', background: 'none', border: 'none', color: '#555', fontSize: '13px', cursor: 'pointer' }}>
          Log out
        </button>
      </div>
    </div>
  );
}

// Signed-in area: one shared app shell, so the sidebar stays mounted while navigating
function PrivateLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  useEffect(() => {
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 300));
    idle(() => APP_PAGES.forEach(page => page.preload().catch(() => {})));
  }, []);
  if (loading) return null;
  if (!user) {
    // Invite links opened while signed out: back to the invite after signing in
    rememberReturnTo(location.pathname);
    return <Navigate to="/login" />;
  }
  if (user.timeout && new Date(user.timeout.expires_at) > new Date()) {
    return <TimeoutScreen timeout={user.timeout} />;
  }
  if (user.twofa_required_setup) {
    return <TwoFactorRequiredScreen />;
  }
  return (
    <AppLayout>
      <Suspense fallback={null}><Outlet /></Suspense>
    </AppLayout>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/adminpage" element={<AdminPage />} />
        <Route path="/info" element={<InfoPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<Navigate to="/dm" replace />} />
        <Route element={<PrivateLayout />}>
          <Route path="/profile/:username" element={<Profile />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/edit-profile" element={<EditProfile />} />
          <Route path="/dm" element={<EmptyState />} />
          <Route path="/dm/:conversationId" element={<ConversationPage />} />
          <Route path="/dm/user/:userId" element={<UserChatPage />} />
          <Route path="/servers/:nodeId" element={<ServerHome />} />
          <Route path="/servers/:nodeId/:channelId" element={<ChannelPage />} />
          <Route path="/invite/:nodeId/:code" element={<InvitePage />} />
        </Route>
        {/* Search and message requests live in the sidebar now */}
        <Route path="/search" element={<Navigate to="/dm?search=1" replace />} />
        <Route path="/dm/new" element={<Navigate to="/dm?search=1" replace />} />
        <Route path="/notifications" element={<Navigate to="/dm" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function AppShell() {
  const { loading, user } = useAuth();
  const [minPassed, setMinPassed] = useState(false);
  const [splashFading, setSplashFading] = useState(false);
  const [splashHidden, setSplashHidden] = useState(false);
  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setMinPassed(true), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!loading && minPassed && !splashFading && !splashHidden) {
      setSplashFading(true);
      setTimeout(() => setSplashHidden(true), 350);
    }
  }, [loading, minPassed]);

  useEffect(() => {
    if (loading || !user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get('/api/announcements/current', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.data) setAnnouncement(r.data); })
      .catch(() => {});
  }, [loading, user]);

  return (
    <>
      {!splashHidden && <SplashScreen fading={splashFading} />}
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
      {announcement && <UpdatePopup announcement={announcement} onDismiss={() => setAnnouncement(null)} />}
    </>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <RealtimeProvider>
          <ServersProvider>
            <BrowserRouter>
              <AppShell />
            </BrowserRouter>
          </ServersProvider>
        </RealtimeProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}
